"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { B2BLanguageItem, getB2BLanguageSwitchConfig } from "./b2bLanguageSwitch";

interface LanguageDataNode {
  nodeName?: string;
  children?: B2BLanguageItem[];
}

interface B2BLanguageSwitcherProps {
  enabled?: boolean;
  languageData?: {
    children?: LanguageDataNode[];
  };
}

function getLanguageItems(
  languageData?: B2BLanguageSwitcherProps["languageData"]
): B2BLanguageItem[] {
  const children = languageData?.children;
  if (!Array.isArray(children)) return [];

  for (const child of children) {
    if (child?.nodeName === "languageCodeMapping" || child?.nodeName === "languages") {
      return child.children ?? [];
    }
  }
  return [];
}

const B2BLanguageSwitcher = ({ enabled = false, languageData }: B2BLanguageSwitcherProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const languageItems = useMemo(() => getLanguageItems(languageData), [languageData]);

  const switchConfig = useMemo(() => {
    if (!enabled || !pathname) {
      return null;
    }

    const queryString = searchParams?.toString();
    const search = queryString ? `?${queryString}` : "";
    return getB2BLanguageSwitchConfig(pathname, languageItems, search);
  }, [enabled, pathname, searchParams, languageItems]);
  const dropdownIconBaseClass = String.raw`icomoon ml-[2px] text-[10px] transition-transform duration-300 before:content-['\e981']`;
  const dropdownIconClass = `${dropdownIconBaseClass} ${
    isOpen ? "-rotate-180" : "group-hover:-rotate-180"
  }`;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  if (!switchConfig) {
    return null;
  }

  const currentOption = switchConfig.options.find(
    ({ value }) => value === switchConfig.currentLanguage
  );
  const currentLanguageLabel = currentOption?.label ?? "English";
  const isWithin = (element: HTMLElement | null, target: EventTarget | null) =>
    !!element && target instanceof Node && element.contains(target);

  return (
    <div ref={containerRef} data-testid="b2b-language-switcher" className="relative hidden md:flex">
      <button
        ref={triggerRef}
        type="button"
        aria-label="Select page language"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className="group flex cursor-pointer flex-col items-center gap-1 px-3 pt-[14px] pb-[30px] text-xs leading-[14px] font-medium text-white transition-colors hover:bg-[#001326]"
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={(event) => {
          if (!isWithin(dropdownRef.current, event.relatedTarget)) {
            setIsOpen(false);
          }
        }}
        onClick={() => setIsOpen((open) => !open)}
      >
        <i
          aria-hidden="true"
          className="bf-icon-vernac-language inline-flex h-6 w-6 items-center justify-center text-[24px] leading-none"
        />
        <span className="flex items-center text-[12px] leading-[14px] whitespace-nowrap">
          <span>{currentLanguageLabel}</span>
          <i aria-hidden="true" className={dropdownIconClass} />
        </span>
      </button>

      {isOpen ? (
        <div
          ref={dropdownRef}
          role="menu"
          tabIndex={-1}
          aria-label="Language options"
          className="absolute top-full left-0 z-20 mt-0 min-w-[246px] overflow-hidden rounded-t-none rounded-b-2xl bg-[#03162b] py-2 text-white shadow-[0_18px_40px_rgba(0,0,0,0.35)]"
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={(event) => {
            if (!isWithin(triggerRef.current, event.relatedTarget)) {
              setIsOpen(false);
            }
          }}
        >
          {switchConfig.options.map(({ value, href, menuLabel }) => {
            const isSelected = value === switchConfig.currentLanguage;

            return (
              <button
                key={value}
                type="button"
                role="menuitemradio"
                aria-checked={isSelected}
                className={`flex w-full cursor-pointer items-center gap-4 px-6 py-4 text-left text-[12px] leading-[14px] font-medium text-white transition-colors hover:bg-white/10 focus-visible:bg-white/10 focus-visible:outline-none ${
                  isSelected ? "bg-[#0b2743]" : ""
                }`}
                onClick={() => {
                  setIsOpen(false);
                  if (!isSelected) {
                    window.location.assign(href);
                  }
                }}
              >
                <span
                  aria-hidden="true"
                  className={`inline-flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                    isSelected ? "border-[#ff7a00]" : "border-white bg-white"
                  }`}
                >
                  {isSelected ? (
                    <span className="block h-[6px] w-[6px] rounded-full bg-[#ff7a00]" />
                  ) : null}
                </span>
                <span>{menuLabel}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default B2BLanguageSwitcher;
